import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save,
  Share2,
  Plus,
  FileText,
  BarChart3,
  Image,
  Link,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useToast } from '../components/Toast';
import CompanyDataAuthorizationPanel from '../components/CompanyDataAuthorizationPanel';
import {
  isAuthorizationRequiredError,
  readApiResponseError,
} from '../services/companyDataAuthorizationService';

const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${protocol}//${hostname}:8001`;
  }
  return 'http://localhost:8001';
};

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('proposalcraft_access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

interface ProjectData {
  id: string;
  title: string;
  status: string;
  progress: number;
  lastSaved: string;
  content: string;
  grant_id: string;
  company_id: string;
  grant_title?: string;
  company_name?: string;
  sections: Array<{
    id: string;
    title: string;
    status: 'completed' | 'in-progress' | 'pending';
    progress: number;
    content: string;
  }>;
}

const ProjectWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState<number>(0);
  const [editContent, setEditContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authorizationId, setAuthorizationId] = useState<string | null>(null);

  const invalidateAuthorizationIfNeeded = (
    apiError: { status: number; code?: string; message: string } | null,
  ): boolean => {
    const invalid =
      apiError !== null && isAuthorizationRequiredError(apiError);
    if (invalid) setAuthorizationId(null);
    return invalid;
  };

  const [projectData, setProjectData] = useState<ProjectData>({
    id: id || '',
    title: '',
    status: '작성중',
    progress: 0,
    lastSaved: '',
    content: '',
    grant_id: '',
    company_id: '',
    sections: [],
  });

  const fetchProposal = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/proposals/${id}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error('제안서를 불러올 수 없습니다');

      const data = await response.json();

      // 본문 내용을 섹션으로 파싱 (마크다운 # 기준)
      const content = data.content || '';
      const sectionRegex = /^#{1,2}\s+(.+)$/gm;
      const parts: Array<{ title: string; content: string }> = [];
      let lastIndex = 0;
      let match;

      while ((match = sectionRegex.exec(content)) !== null) {
        if (parts.length > 0) {
          parts[parts.length - 1].content = content.substring(lastIndex, match.index).trim();
        }
        parts.push({ title: match[1], content: '' });
        lastIndex = match.index + match[0].length;
      }
      if (parts.length > 0) {
        parts[parts.length - 1].content = content.substring(lastIndex).trim();
      }

      // 섹션이 없으면 전체를 하나의 섹션으로
      type SectionEntry = { id: string; title: string; status: 'completed' | 'in-progress' | 'pending'; progress: number; content: string };
      const sections: SectionEntry[] = parts.length > 0
        ? parts.map((p, i) => ({
            id: String(i + 1),
            title: p.title,
            status: (p.content.length > 50 ? 'completed' : p.content.length > 0 ? 'in-progress' : 'pending') as 'completed' | 'in-progress' | 'pending',
            progress: p.content.length > 50 ? 100 : p.content.length > 0 ? 50 : 0,
            content: p.content,
          }))
        : [{
            id: '1',
            title: '본문',
            status: (content.length > 0 ? 'in-progress' : 'pending') as 'in-progress' | 'pending',
            progress: content.length > 0 ? 50 : 0,
            content: content,
          }];

      const completedSections = sections.filter((s: { status: string }) => s.status === 'completed').length;
      const overallProgress = Math.round((completedSections / Math.max(sections.length, 1)) * 100);

      setProjectData({
        id: data.id || id,
        title: data.title || '제안서',
        status: data.status || '작성중',
        progress: data.progress || overallProgress,
        lastSaved: data.updated_at ? new Date(data.updated_at).toLocaleString('ko-KR') : '',
        content: content,
        grant_id: data.grant_id || data.grant?.id || '',
        company_id: data.company_id || data.company?.id || '',
        grant_title: data.grant_title || data.grant?.title || '',
        company_name: data.company_name || data.company?.name || '',
        sections,
      });

      if (sections.length > 0) {
        setEditContent(sections[0].content);
      }
    } catch (err: any) {
      console.error('Failed to load proposal:', err);
      setError(err.message || '제안서를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProposal();
  }, [fetchProposal]);

  useEffect(() => {
    setEditContent(projectData.sections[activeSection]?.content || '');
  }, [activeSection, projectData.sections]);

  const handleSave = async () => {
    if (!id) return;
    if (
      !projectData.company_id ||
      !projectData.grant_id ||
      !authorizationId
    ) {
      toast.error(
        '저장 전에 기업 자료 처리 동의를 선택하고 제안서 귀속 정보를 확인해 주세요.',
      );
      return;
    }
    setSaving(true);

    try {
      // 현재 편집 중인 섹션 업데이트
      const updatedSections = projectData.sections.map((s, i) =>
        i === activeSection ? { ...s, content: editContent } : s
      );
      const fullContent = updatedSections.map(s => `## ${s.title}\n\n${s.content}`).join('\n\n');

      const response = await fetch(`${getApiBaseUrl()}/api/proposals/${id}/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: projectData.title,
          content: fullContent,
          grant_id: projectData.grant_id,
          company_id: projectData.company_id,
          authorization_id: authorizationId,
        }),
      });

      if (response.ok) {
        setProjectData(prev => ({
          ...prev,
          sections: updatedSections,
          lastSaved: new Date().toLocaleString('ko-KR'),
          content: fullContent,
        }));
        toast.success('저장되었습니다');
      } else {
        throw await readApiResponseError(response, '저장에 실패했습니다.');
      }
    } catch (caught: unknown) {
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      toast.error(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 동의를 다시 확인해 주세요.'
          : apiError?.message || '저장에 실패했습니다.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleExportDocument = async (format: string = 'docx') => {
    if (!projectData.company_id || !authorizationId) {
      toast.error(
        '내보내기 전에 기업 자료 처리 동의를 선택해 주세요.',
      );
      return;
    }

    try {
      const updatedSections = projectData.sections.map((section, index) =>
        index === activeSection
          ? { ...section, content: editContent }
          : section,
      );
      const fullContent = updatedSections
        .map((section) => `## ${section.title}\n\n${section.content}`)
        .join('\n\n');
      const response = await fetch(`${getApiBaseUrl()}/api/proposals/export/document`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          proposal_data: {
            title: projectData.title,
            content: fullContent,
            company_name: projectData.company_name || '',
            sections: updatedSections.map((section) => section.title),
          },
          company_id: projectData.company_id,
          authorization_id: authorizationId,
          format: format,
        }),
      });

      if (!response.ok) {
        throw await readApiResponseError(
          response,
          '문서 내보내기에 실패했습니다.',
        );
      }
      const data = await response.json();

      if (data.success && data.content) {
        let blob: Blob;
        if (data.is_base64) {
          const byteChars = atob(data.content);
          const byteArray = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) {
            byteArray[i] = byteChars.charCodeAt(i);
          }
          blob = new Blob([byteArray], { type: data.content_type });
        } else {
          blob = new Blob([data.content], { type: data.content_type || 'text/plain' });
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `document.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (caught: unknown) {
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      toast.error(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 동의를 다시 확인해 주세요.'
          : apiError?.message || '문서 내보내기에 실패했습니다.',
      );
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in-progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'pending': return <AlertCircle className="h-4 w-4 text-gray-400" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in-progress': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
        <span className="ml-3 text-gray-600">제안서를 불러오는 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">
          돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {projectData.company_id && (
        <CompanyDataAuthorizationPanel
          companyId={projectData.company_id}
          value={authorizationId}
          onChange={setAuthorizationId}
        />
      )}

      <div className="flex h-full space-x-6">
      {/* 왼쪽 사이드바 */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{projectData.title}</h1>
          {projectData.grant_title && (
            <p className="text-sm text-gray-500 mb-1">{projectData.grant_title}</p>
          )}
          {projectData.company_name && (
            <p className="text-sm text-gray-500 mb-2">{projectData.company_name}</p>
          )}
          <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(projectData.status)}`}>
              {projectData.status}
            </span>
            {projectData.lastSaved && (
              <>
                <span>·</span>
                <span>저장: {projectData.lastSaved}</span>
              </>
            )}
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">전체 진행률</span>
              <span className="font-medium">{projectData.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${projectData.progress}%` }}
              />
            </div>
          </div>

          <div className="flex space-x-2">
            <button onClick={handleSave} disabled={saving || !authorizationId} className="btn-primary flex-1">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {saving ? '저장 중...' : '저장'}
            </button>
            <button className="btn-secondary">
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* 섹션 목록 */}
        <div className="flex-1 p-6 overflow-y-auto">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">섹션</h2>
          <div className="space-y-2">
            {projectData.sections.map((section, index) => (
              <div
                key={section.id}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  activeSection === index
                    ? 'bg-emerald-50 border border-emerald-200'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => setActiveSection(index)}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-gray-900 text-sm">{section.title}</h3>
                  {getStatusIcon(section.status)}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1">
                  <div
                    className="bg-emerald-500 h-1 rounded-full"
                    style={{ width: `${section.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 메인 에디터 */}
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {projectData.sections[activeSection]?.title || '섹션'}
              </h2>
              <span className="text-sm text-gray-500">
                {projectData.sections[activeSection]?.progress || 0}% 완료
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handleExportDocument('docx')}
                disabled={!authorizationId}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-l-lg hover:bg-blue-700"
              >
                DOCX
              </button>
              <button
                onClick={() => handleExportDocument('pdf')}
                disabled={!authorizationId}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700"
              >
                PDF
              </button>
              <button
                onClick={() => handleExportDocument('markdown')}
                disabled={!authorizationId}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
              >
                MD
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6">
          <textarea
            className="w-full h-full min-h-[400px] p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono text-sm"
            placeholder="이 섹션의 내용을 작성하세요..."
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
          />
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProjectWorkspace;
